// Databus approval API (ADR 0023 Sprint 6c).
//
// Thin wrappers around the approval engine HTTP surface built in Sprint 6b:
//   POST /api/v1/approvals                        — submit new (maker)
//   POST /api/v1/approvals/{public_id}/approve    — approve (checker; maker≠checker)
//   POST /api/v1/approvals/{public_id}/reject     — reject (checker)
//   POST /api/v1/approvals/{public_id}/apply      — mark applied (approved→applied)
//   GET  /api/v1/approvals                        — list pending for caller's tenant
//   GET  /api/v1/approvals/{public_id}            — get one
//
// Entity types relevant to the databus activation surface (mirrors
// entities.ApprovalEntityType from the Go backend):
//   mapping_activation, adapter_activation, adapter_resume
//
// Security: tenant scope always derived from the caller's JWT by the
// backend (never from URL params). Cross-tenant read → 404.
//
// The /databus/mappings/:id/activate and /databus/adapters/:id/resume
// endpoints now accept an optional approval_public_id body field; the
// backend 409s if omitted when the tenant's maker-checker policy requires
// approval (Sprint 6b backend gate).
import { apiJson } from '@/api/client'

// ---- Wire shapes -------------------------------------------------------

/** Maps to approval.approval_state Postgres enum. */
export type ApprovalState =
  | 'draft'
  | 'submitted'
  | 'approved'
  | 'rejected'
  | 'applied'
  | 'superseded'
  | 'rolled_back'

/** Maps to approval.entity_type Postgres enum (databus-relevant subset). */
export type ApprovalEntityType =
  | 'mapping_activation'
  | 'adapter_activation'
  | 'adapter_resume'
  | 'tenant_activation'
  | 'connector_change'
  | 'ingestion_credential_change'
  | 'subdomain_change'
  | 'tenant_suspension'
  | 'tenant_archival'

/** Wire shape returned by the approval API. BeforeState/AfterState are
 *  intentionally omitted from the response by the backend (AF-007). */
export interface ApprovalRecord {
  id: number
  public_id: string
  tenant_id?: number | null
  actor_scope_type: string
  entity_type: ApprovalEntityType
  entity_id: string
  action: string
  state: ApprovalState
  maker_id: number
  checker_id?: number | null
  reason?: string
  decision_reason?: string
  submitted_at?: string | null
  decided_at?: string | null
  applied_at?: string | null
  created_at: string
  updated_at: string
}

export interface SubmitApprovalInput {
  entity_type: ApprovalEntityType
  /** Stable UUID of the entity being approved. */
  entity_id: string
  action: string
  reason?: string
  /** JSON-serialisable before-state. AF-007: no PII. */
  before_state?: unknown
  /** JSON-serialisable after-state. AF-007: no PII. */
  after_state?: unknown
}

// ---- Fetchers ---------------------------------------------------------

export async function submitApproval(input: SubmitApprovalInput): Promise<ApprovalRecord> {
  return apiJson<ApprovalRecord>('/approvals', {
    method: 'POST',
    body: input,
  })
}

export async function approveApproval(
  publicId: string,
  note?: string,
): Promise<ApprovalRecord> {
  return apiJson<ApprovalRecord>(`/approvals/${encodeURIComponent(publicId)}/approve`, {
    method: 'POST',
    body: note ? { note } : {},
  })
}

export async function rejectApproval(
  publicId: string,
  note?: string,
): Promise<ApprovalRecord> {
  return apiJson<ApprovalRecord>(`/approvals/${encodeURIComponent(publicId)}/reject`, {
    method: 'POST',
    body: note ? { note } : {},
  })
}

export async function applyApproval(publicId: string): Promise<ApprovalRecord> {
  return apiJson<ApprovalRecord>(`/approvals/${encodeURIComponent(publicId)}/apply`, {
    method: 'POST',
  })
}

export async function listApprovals(
  state?: ApprovalState,
  limit = 50,
): Promise<{ approvals: ApprovalRecord[] }> {
  const params = new URLSearchParams()
  if (state) params.set('state', state)
  if (limit !== 50) params.set('limit', String(limit))
  const qs = params.toString()
  return apiJson<{ approvals: ApprovalRecord[] }>(
    `/approvals${qs ? `?${qs}` : ''}`,
  )
}

export async function getApproval(publicId: string): Promise<ApprovalRecord> {
  return apiJson<ApprovalRecord>(`/approvals/${encodeURIComponent(publicId)}`)
}

// ---- Gated activate/resume (Sprint 6b backend, approval_public_id required) ----

/** Activate a mapping. Requires an applied approval public_id per Sprint 6b backend gate.
 *  The backend returns 409 with code "approval_required" if omitted and the tenant
 *  policy requires approval, or if the approval is not in 'applied' state. */
export async function activateMappingGated(
  mappingId: number,
  approvalPublicId: string,
): Promise<{ id: number; status: string }> {
  return apiJson<{ id: number; status: string }>(
    `/databus/mappings/${mappingId}/activate`,
    { method: 'POST', body: { approval_public_id: approvalPublicId } },
  )
}

/** Resume a paused adapter. Requires an applied approval_public_id per Sprint 6b gate. */
export async function resumeAdapterGated(
  adapterId: number,
  approvalPublicId: string,
): Promise<{ id: number; status: string }> {
  return apiJson<{ id: number; status: string }>(
    `/databus/adapters/${adapterId}/resume`,
    { method: 'POST', body: { approval_public_id: approvalPublicId } },
  )
}

// ---- Checklist (wired Sprint 6c — backend HTTP handler added by go-specialist) -----
//
// The checklist engine (internal/approval/checklist.go: EvaluateChecklist,
// ChecklistGatePassed, SubmitForActivation) is exposed via the routes below.
// MSW handlers in mocks-approvals.ts mirror these paths for offline dev and tests.

export interface ChecklistItemRecord {
  item_key: string
  required: boolean
  state: 'pending' | 'passed' | 'failed' | 'skipped' | 'na'
  detail?: { note?: string } | null
  last_evaluated_at?: string | null
}

export interface ChecklistRecord {
  public_id: string
  tenant_id: number
  status: 'in_progress' | 'complete' | 'submitted' | 'approved' | 'rejected'
  total_items: number
  completed_items: number
  submitted_by?: number | null
  submitted_at?: string | null
  approval_public_id?: string | null
  items: ChecklistItemRecord[]
  created_at: string
  updated_at: string
}

/** Evaluate the activation checklist for a tenant and return fresh item states. */
export async function evaluateChecklist(tenantId: number): Promise<ChecklistRecord> {
  return apiJson<ChecklistRecord>(
    `/tenants/${tenantId}/checklist/evaluate`,
    { method: 'POST' },
  )
}

/** Get the current checklist state for a tenant. */
export async function getChecklist(tenantId: number): Promise<ChecklistRecord> {
  return apiJson<ChecklistRecord>(`/tenants/${tenantId}/checklist`)
}

/** Submit for activation — creates a tenant_activation approval linked to the checklist. */
export async function submitForActivation(
  tenantId: number,
  reason: string,
): Promise<ApprovalRecord> {
  return apiJson<ApprovalRecord>(
    `/tenants/${tenantId}/checklist/submit`,
    { method: 'POST', body: { reason } },
  )
}
